/**
 * "ARQUIVO VIVO" no painel: o `timeline_view.dart`.
 *
 * A faixa larga que fica entre a prévia da agenda e a caixa de cartas na ordem
 * do painel de lá (`dashboard_screen.dart:499`), e mais uma das poucas sem
 * `so:` — não desliga nos ajustes. É um carrossel de marcos em cartão de filme:
 * tira perfurada em cima e embaixo, as fotos passando sozinhas de três em três
 * segundos, e o cartão inteiro andando de cinco em cinco. Tocar num deles abre
 * o `MemoryDetailOverlay`, que aqui é a folha do meio da tela.
 *
 * As palavras miúdas ficam como estão lá — `'ARQUIVO VIVO'`, `'Living Archive'`,
 * `"Memory by … ♡"` — e a data do cartão fica em inglês pelo mesmo motivo que o
 * `americana` explica: o Flutter nunca carregou o locale, então é `en_US` que o
 * outro app mostra.
 *
 * O que o navegador obrigou a mudar:
 *
 * - **A volta do carrossel é uma fita de três cópias, e não um índice
 *   infinito.** Lá o `PageController` abre na página 5000 e o item sai de
 *   `marcos[index % marcos.length]`. Aqui quem rola é o próprio navegador, e um
 *   trilho infinito não existe: a lista é desenhada três vezes, a rolagem
 *   começa na cópia do meio e, quando o descanso cai numa das pontas, o trilho
 *   pula uma cópia inteira. O quadro é idêntico, então o pulo não se vê.
 * - **O deslize é o do navegador, e não 1500ms de `easeInOutCubic`.** O
 *   `scrollTo({behavior: 'smooth'})` traz de graça o encaixe, o arrasto de
 *   dedo e o trackpad; refazer isso à mão com `requestAnimationFrame` seria
 *   brigar com o `scroll-snap` a cada quadro para ganhar meio segundo de
 *   vagar. A conta do vizinho encolhido é a mesma de lá, essa sim ao pé da
 *   letra: `1 - distância * 0.15`, preso entre 0 e 1, e a opacidade entre 0,5
 *   e 1.
 * - **O carrossel para sozinho quando alguém está mexendo.** Lá o `Timer` roda
 *   por cima do gesto. Aqui, puxar o cartão e ver o trilho ser arrancado da mão
 *   cinco segundos depois é briga; então enquanto o ponteiro está em cima, ou
 *   algo lá dentro tem o foco, ou a aba está escondida, o relógio espera. Com
 *   `prefers-reduced-motion` ele nem começa: o trilho continua rolável à mão.
 * - **O vão de 520 vazios não é copiado.** Lá o cabeçalho fica de pé sobre um
 *   `SizedBox(height: 520)` com um `SizedBox.shrink()` dentro quando não há
 *   marco nenhum. Um título com um buraco embaixo é defeito nos dois apps;
 *   durante todo o porte a fatia inteira se apagava, como a de "Neste dia".
 *   **Agora ela não se apaga mais**, e a troca tem motivo: desde que existe a
 *   folha de guardar um marco, sumir com a faixa esconderia justamente a porta
 *   de criar o primeiro. No lugar do vão fica o convite — o mesmo `Vazio` das
 *   outras telas, em cartão baixo. Cheia de marcos, a faixa é a de sempre.
 * - **O "+" fica na tarja.** Lá não há por onde guardar um marco: o
 *   `addMilestone` existe e ninguém o chama. O botão entra no fim da faixa, e
 *   não dentro do carrossel, porque com zero marcos o carrossel não existe.
 * - **Cormorant vai até 700.** O `cormorantBlack` (w900) do nome da faixa não
 *   tem arquivo aqui: a casa carrega Regular, SemiBold e Bold.
 * - **O cartão sem foto mostra o traço da categoria.** Lá o `PageView` com zero
 *   itens desenha nada e sobra um retângulo de vidro. O mesmo vazio, com o que
 *   ele é escrito por cima bem de leve.
 * - **A folha do detalhe é a `<dialog>` da casa.** O `showGeneralDialog` de lá
 *   vira a `Folha` no meio da tela, que já prende o teclado, já fecha no Esc e
 *   já escurece o fundo. O X do canto continua, porque lá ele existe.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  COLECOES,
  americana,
  iconeDoMarco,
  marcoDeMapa,
  marcosEmOrdem,
  numerica,
  traduzir,
  usarColecao,
  usarEstadoDoArmazem,
  // Curto aqui dentro, que é o vocabulário desta tela: o `Marco` da casa — o de
  // "namorando", "casados" — não aparece nesta ala.
  type MarcoDoArquivo as Marco,
} from '@nosso-sempre/shared';
import { Folha } from './Folha';
import { FolhaMarco } from './FolhaMarco';
import { Icone } from './Icone';
import { Esqueleto, Vazio } from './Pecas';
import './arquivoVivo.css';

/** O `viewportFraction: 0.85` do `PageController` de lá. */
const FATIA = 0.85;

/** As três cópias que fazem a volta ser invisível. */
const COPIAS = 3;

/** O `Timer.periodic(Duration(seconds: 5))` do carrossel. */
const ESPERA = 5000;

/** O `Timer.periodic(Duration(seconds: 3))` das fotos dentro do cartão. */
const ESPERA_DA_FOTO = 3000;

/** Quanto o esqueleto ocupa do palco de 520 enquanto os marcos não chegam. */
const ALTURA_DA_ESPERA = 440;

export function ArquivoVivo() {
  const docs = usarColecao<Record<string, unknown>>(COLECOES.marcos);
  const { pronto, erro } = usarEstadoDoArmazem();
  const [aberto, setAberto] = useState<Marco | null>(null);
  const [guardando, setGuardando] = useState(false);

  const marcos = useMemo(() => marcosEmOrdem(docs.map((d) => marcoDeMapa(d.id, d.dados))), [docs]);

  /** Sem marco nenhum não há palco: fica o convite de guardar o primeiro. */
  const vazio = pronto && !erro && marcos.length === 0;

  return (
    <section className="arquivo" aria-labelledby="arquivo-vivo">
      <p className="arquivo-tarja">
        <span className="arquivo-fio" aria-hidden="true" />
        <span className="arquivo-selo" id="arquivo-vivo">
          {traduzir('ARQUIVO VIVO')}
        </span>
        <span className="arquivo-fio" aria-hidden="true" />
        <button
          type="button"
          className="arquivo-por"
          onClick={() => setGuardando(true)}
          aria-label={traduzir('Guardar um marco')}
        >
          <Icone nome="mais" tamanho={18} />
        </button>
      </p>

      {vazio ? (
        <Vazio
          icone="estrela"
          titulo={traduzir('Nenhum marco ainda')}
          recado={traduzir('O primeiro capítulo de vocês começa aqui.')}
          acao={
            <button className="botao" type="button" onClick={() => setGuardando(true)}>
              <Icone nome="mais" />
              {traduzir('Guardar um marco')}
            </button>
          }
        />
      ) : (
        <div className="arquivo-palco">
          {erro ? (
            <p className="arquivo-recado">
              <Icone nome="nuvem-cortada" tamanho={32} />
              <span>{traduzir('Não deu para carregar as memórias')}</span>
            </p>
          ) : !pronto ? (
            <div className="arquivo-espera">
              <Esqueleto quantos={1} altura={ALTURA_DA_ESPERA} />
              <span className="arquivo-carregando">{traduzir('Carregando as memórias...')}</span>
            </div>
          ) : (
            <Carrossel marcos={marcos} aoAbrir={setAberto} />
          )}
        </div>
      )}

      <FolhaDoMarco marco={aberto} aoFechar={() => setAberto(null)} />
      <FolhaMarco aberta={guardando} aoFechar={() => setGuardando(false)} />
    </section>
  );
}

/** O `PageView.builder` infinito, com a fita de três cópias no lugar do 5000. */
function Carrossel({ marcos, aoAbrir }: { marcos: Marco[]; aoAbrir: (m: Marco) => void }) {
  const trilho = useRef<HTMLUListElement>(null);
  /** Em que quadro da fita o trilho descansa, para o resize não perder o lugar. */
  const alvo = useRef(0);

  const quantos = marcos.length;

  const fileira = useMemo(
    () => Array.from({ length: COPIAS * quantos }, (_, i) => marcos[i % quantos]),
    [marcos, quantos],
  );

  useEffect(() => {
    const el = trilho.current;
    if (!el) return;

    // O passo é a largura de um quadro, e é ele que faz `--pagina` valer o
    // mesmo que o `_pageController.page` de lá: com 7,5% de folga de cada lado
    // e o quadro ocupando a área útil inteira, centrar o quadro `i` é rolar
    // exatamente `i * passo`.
    const passo = () => el.clientWidth * FATIA;

    alvo.current = quantos;
    el.scrollLeft = alvo.current * passo();
    el.style.setProperty('--pagina', String(alvo.current));

    let quieto = 0;

    /** Volta para a cópia do meio quando o descanso cai numa das pontas. */
    const normalizar = () => {
      const p = passo();
      if (p <= 0) return;
      const atual = Math.round(el.scrollLeft / p);
      const i = atual < quantos ? atual + quantos : atual >= 2 * quantos ? atual - quantos : atual;
      alvo.current = i;
      if (i !== atual) el.scrollLeft = i * p;
    };

    const naRolagem = () => {
      const p = passo();
      if (p > 0) el.style.setProperty('--pagina', (el.scrollLeft / p).toFixed(4));
      window.clearTimeout(quieto);
      quieto = window.setTimeout(normalizar, 160);
    };

    let pausa = false;
    const pausar = () => {
      pausa = true;
    };
    const soltar = () => {
      pausa = false;
    };

    el.addEventListener('scroll', naRolagem, { passive: true });
    el.addEventListener('pointerenter', pausar);
    el.addEventListener('pointerleave', soltar);
    el.addEventListener('focusin', pausar);
    el.addEventListener('focusout', soltar);

    const parado = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const relogio = parado
      ? 0
      : window.setInterval(() => {
          if (pausa || document.hidden) return;
          const p = passo();
          if (p <= 0) return;
          alvo.current = Math.round(el.scrollLeft / p) + 1;
          el.scrollTo({ left: alvo.current * p, behavior: 'smooth' });
        }, ESPERA);

    const noTamanho = () => {
      el.scrollLeft = alvo.current * passo();
    };
    window.addEventListener('resize', noTamanho);

    return () => {
      window.clearTimeout(quieto);
      if (relogio) window.clearInterval(relogio);
      el.removeEventListener('scroll', naRolagem);
      el.removeEventListener('pointerenter', pausar);
      el.removeEventListener('pointerleave', soltar);
      el.removeEventListener('focusin', pausar);
      el.removeEventListener('focusout', soltar);
      window.removeEventListener('resize', noTamanho);
    };
  }, [quantos]);

  return (
    <ul
      className="arquivo-trilho"
      ref={trilho}
      tabIndex={0}
      aria-label={traduzir('Marcos guardados')}
    >
      {fileira.map((m, i) => {
        // Só a cópia do meio existe para quem lê a tela em voz alta: as outras
        // duas são a mesma memória, e ouvi-la três vezes seria ruído.
        const copia = i < quantos || i >= 2 * quantos;
        return (
          <li key={`${m.id}-${i}`} style={{ ['--i' as string]: String(i) }}>
            <FichaDeMarco marco={m} aoAbrir={aoAbrir} copia={copia} />
          </li>
        );
      })}
    </ul>
  );
}

/** O `_MilestoneCarouselCard`: 320 por 500, tira de filme em cima e embaixo. */
function FichaDeMarco({
  marco,
  aoAbrir,
  copia,
}: {
  marco: Marco;
  aoAbrir: (m: Marco) => void;
  copia: boolean;
}) {
  return (
    <button
      type="button"
      className="arquivo-ficha"
      onClick={() => aoAbrir(marco)}
      aria-hidden={copia || undefined}
      tabIndex={copia ? -1 : undefined}
    >
      <TiraDeFilme />

      <span className="arquivo-janela">
        <Fotos marco={marco} />
        <span className="arquivo-dizeres">
          <span className="arquivo-nome">{marco.titulo}</span>
          <span className="arquivo-data">{americana(marco.quando)}</span>
          <span className="arquivo-conta">{marco.descricao}</span>
        </span>
      </span>

      <TiraDeFilme />
    </button>
  );
}

/** O `_buildFilmStripEdge`: oito furos quadrados de 12, com 20 de altura. */
function TiraDeFilme() {
  return (
    <span className="arquivo-tira" aria-hidden="true">
      {Array.from({ length: 8 }, (_, i) => (
        <span key={i} />
      ))}
    </span>
  );
}

/** O `PageView` de dentro do cartão, que troca de foto sozinho. */
function Fotos({ marco }: { marco: Marco }) {
  const [n, setN] = useState(0);
  const quantas = marco.imagens.length;

  useEffect(() => {
    if (quantas <= 1) return;
    const id = window.setInterval(() => setN((x) => (x + 1) % quantas), ESPERA_DA_FOTO);
    return () => window.clearInterval(id);
  }, [quantas]);

  if (quantas === 0) {
    return (
      <span className="arquivo-foto vazia" aria-hidden="true">
        <Icone nome={iconeDoMarco(marco.tipo)} tamanho={44} />
      </span>
    );
  }

  return (
    <span className="arquivo-foto">
      <span className="arquivo-rolo" style={{ transform: `translateX(-${n * 100}%)` }}>
        {marco.imagens.map((endereco, i) => (
          // Sem texto alternativo de propósito: o nome, a data e a conta do
          // marco estão logo abaixo, e repetir o endereço da foto viraria
          // barulho para quem ouve a tela.
          <img key={`${endereco}-${i}`} src={endereco} alt="" loading="lazy" decoding="async" />
        ))}
      </span>
    </span>
  );
}

/** O `MemoryDetailOverlay`, na folha do meio da tela. */
function FolhaDoMarco({ marco, aoFechar }: { marco: Marco | null; aoFechar: () => void }) {
  return (
    <Folha
      aberta={marco !== null}
      aoFechar={aoFechar}
      titulo={marco?.titulo ?? ''}
      recado={marco ? numerica(marco.quando) : undefined}
      centro
      classe="folha-marco"
    >
      {marco ? (
        <>
          <button
            type="button"
            className="marco-fechar"
            onClick={aoFechar}
            aria-label={traduzir('Fechar')}
          >
            <Icone nome="fechar" tamanho={20} />
          </button>

          <div className="marco-texto">
            {marco.autor ? (
              <p className="marco-autor">
                {traduzir('Memory by {autor} ♡', { autor: marco.autor })}
              </p>
            ) : null}
            <p className="marco-conta">{marco.descricao}</p>
          </div>

          <p className="marco-pe">
            <Icone nome="coracao" tamanho={16} preenchido />
            <span>{traduzir('Living Archive')}</span>
            <Icone nome="coracao" tamanho={16} preenchido />
          </p>
        </>
      ) : null}
    </Folha>
  );
}
